import { useFormatters } from '@/hooks/useFormatters';
import ECharts from '@features/echarts';
import { defaultTextStyle, defaultTooltip } from '@features/echarts/utils/baseOption';
import { useMovementData } from '@features/gateCommon/hooks/useMovementData';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { EChartsOption } from 'echarts';
import { useEffect, useMemo, useState, type FC } from 'react';
import { PanelPaper } from './common';
import { Box, Typography } from '@mui/material';

interface Props {
  movementId: number | null;
}

export const PanelChart: FC<Props> = ({ movementId }) => {
  return (
    <PanelPaper sx={{ gridArea: 'chart' }}>
      {movementId && <SuspenseComponent movementId={movementId} />}
    </PanelPaper>
  );
};

const defaultNow = Date.now();

const LINE_COLOR = '#ffffffaa';

const SuspenseComponent: FC<{ movementId: number }> = ({ movementId }) => {
  const { movement } = useMovementData(movementId);
  const [now, setNow] = useState(defaultNow);
  const { formatMetricValue, formatDateTime } = useFormatters();

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const max = useMemo(() => {
    return movement.finishTimestamp ?? now;
  }, [now, movement.finishTimestamp]);

  const min = useMemo(() => {
    return movement.startTimestamp ?? now - 10 * 1000;
  }, [now, movement.startTimestamp]);

  const option = useMemo((): EChartsOption => {
    const positions = movement.positions;

    return {
      grid: {
        id: 'grid',
        bottom: 20,
        top: 25,
        left: 2,
        right: 2,
        containLabel: true,
      },
      tooltip: defaultTooltip,
      textStyle: defaultTextStyle,
      xAxis: {
        id: 'x',
        type: 'time',
        splitNumber: 2,
        max: max,
        min: min,
        axisLine: {
          lineStyle: {
            color: LINE_COLOR,
          },
        },
        axisLabel: {
          color: 'white',
        },
        axisPointer: {
          label: {
            formatter({ value }) {
              if (typeof value === 'number' || value instanceof Date) return formatDateTime(value);
              return value;
            },
          },
        },
      },
      yAxis: {
        boundaryGap: ['10%', '10%'],
        id: 'y',
        position: 'right',
        type: 'value',
        splitNumber: 2,
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255,255,255, 0.2)',
          },
        },
        axisLabel: {
          color: 'white',
          formatter(value) {
            return formatPosition(value, formatMetricValue);
          },
        },
      },
      series: {
        id: 'position',
        xAxisId: 'x',
        yAxisId: 'y',
        name: 'Posición',
        type: 'line',
        symbol: 'circle',
        data: positions,
        tooltip: {
          valueFormatter(value) {
            if (value !== undefined && typeof value === 'number')
              return formatPosition(value, formatMetricValue);
            else return '';
          },
        },
      },
    };
  }, [movement.positions, formatMetricValue, formatDateTime, max, min]);

  if (movement.positions.length === 0) {
    return (
      <Box display="grid" sx={{ placeItems: 'center', height: '100%', textAlign: 'center', p: 1 }}>
        <Typography>Cuando se reciban mensajes de posición se graficarán acá</Typography>
      </Box>
    );
  }

  return <ECharts option={option} />;
};
